/**
 * useFeedback Hook
 * 
 * Handles feedback data fetching for admin review.
 * Encapsulates API calls per Rule 6.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import { Feedback, FeedbackType, FeedbackStatus, FeedbackPriority } from '../shared/types/feedback';

export interface FeedbackStats {
  total: number;
  byType: Record<FeedbackType, number>;
  byStatus: Record<FeedbackStatus, number>;
  byPriority: Record<FeedbackPriority, number>;
}

interface UseFeedbackReturn {
  feedbackList: Feedback[];
  stats: FeedbackStats | null;
  loading: boolean;
  error: string | null;
  filter: FeedbackStatus | 'all';
  setFilter: (status: FeedbackStatus | 'all') => void;
  refresh: () => Promise<void>;
  updateFeedbackStatus: (id: number, status: FeedbackStatus) => Promise<boolean>;
}

const DEFAULT_STATS: FeedbackStats = {
  total: 0,
  byType: { bug: 0, feature_request: 0, ui_ux: 0, performance: 0, accessibility: 0, general: 0, other: 0 },
  byStatus: { submitted: 0, acknowledged: 0, in_progress: 0, resolved: 0, closed: 0 },
  byPriority: { low: 0, medium: 0, high: 0, critical: 0 },
};

export function useFeedback(): UseFeedbackReturn {
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FeedbackStatus | 'all'>('all');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [listResponse, statsResponse] = await Promise.all([
        authApi.getFeedbackList({ status: filter === 'all' ? undefined : filter }),
        authApi.getFeedbackStats(),
      ]);

      if (listResponse.success) {
        setFeedbackList(listResponse.feedback || []);
      }

      if (statsResponse.success && statsResponse.stats) {
        setStats({
          total: statsResponse.stats.total || 0,
          byType: statsResponse.stats.by_type || DEFAULT_STATS.byType,
          byStatus: statsResponse.stats.by_status || DEFAULT_STATS.byStatus,
          byPriority: statsResponse.stats.by_priority || DEFAULT_STATS.byPriority,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load feedback';
      logger.error('Feedback fetch failed', 'feedback', String(err));
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const updateFeedbackStatus = useCallback(async (id: number, status: FeedbackStatus): Promise<boolean> => {
    try {
      const response = await authApi.updateFeedback(id, { status });
      if (response.success) {
        await fetchFeedback();
        return true;
      }
      return false;
    } catch (err) {
      logger.error('Failed to update feedback status', 'feedback', String(err));
      return false;
    }
  }, [fetchFeedback]);

  return {
    feedbackList,
    stats,
    loading,
    error,
    filter,
    setFilter,
    refresh: fetchFeedback,
    updateFeedbackStatus,
  };
}

